import { useState, useEffect } from "react";
import axios from "axios";
import { TrendingUp, IndianRupee, ShoppingCart, BarChart3, Eye, MousePointer, Target, Wallet, Play, X, ChevronLeft, ChevronRight } from "lucide-react";

const DEFAULT_VIDEOS = [
  { id: "blinkit-success", embed_id: "2abNybL6LZQ", brand: "Blinkit Success Story", platforms: "Blinkit" },
  { id: "zavour", embed_id: "17JaaSWiHTg", brand: "Zavour", platforms: "Blinkit — Onboarded" },
  { id: "veshviti", embed_id: "8w1Lv4rot9A", brand: "Veshviti", platforms: "Nykaa Fashion — Onboarded" },
  { id: "naunki", embed_id: "FLnV33mJVx4", brand: "Nunki", platforms: "Myntra Rising Star — Onboarded" },
  { id: "yanx", embed_id: "cBQGgP3hMn8", brand: "Yanx", platforms: "Live on Myntra Rising Star" },
  { id: "redex", embed_id: "ODQWvRc6Mds", brand: "Redex", platforms: "Myntra" },
  { id: "avyanka", embed_id: "tFMM8V4_ZGQ", brand: "Avyanka", platforms: "Myntra" },
  { id: "hotshot", embed_id: "tDoJstRQ05c", brand: "Hotshot", platforms: "Blinkit" },
  { id: "trubliz", embed_id: "ccjzDudu5RE", brand: "Trubliz", platforms: "Myntra" },
  { id: "style-shoes", embed_id: "6011t0Lisjs", brand: "Style Shoes", platforms: "Nykaa Fashion" },
  { id: "flingo", embed_id: "GjSIduoleYc", brand: "Flingo", platforms: "Ajio | Myntra" },
  { id: "sepik", embed_id: "9ktzuyCdnc4", brand: "Sepik Lifescience", platforms: "Swiggy Instamart | Blinkit" },
  { id: "manaki", embed_id: "kGhGWniCse0", brand: "Manaki Enterprises", platforms: "FirstCry" },
  { id: "invite", embed_id: "rw7t2Na6j2g", brand: "Invite Enterprise", platforms: "FirstCry" },
  { id: "nooky", embed_id: "x_VX8MNFA8c", brand: "Nooky", platforms: "Myntra | Pharmeasy | Swiggy Instamart | Blinkit | Big Basket" },
];

const GrowthStory = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [videos, setVideos] = useState(DEFAULT_VIDEOS);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/site/videos`)
      .then((r) => { if (r.data?.length) setVideos(r.data); })
      .catch(() => {});
  }, []);

  // Growth data from the screenshots provided
  const growthMetrics = [
    {
      platform: "Myntra",
      icon: "M",
      color: "from-pink-500 to-rose-500",
      bgColor: "bg-pink-50",
      title: "Payments Summary",
      stats: [
        { label: "Next Payment", value: "₹5,00,567.86", icon: IndianRupee },
        { label: "Last Payment", value: "₹5,08,255.80", icon: Wallet },
        { label: "Total Outstanding", value: "₹29,21,553.07", icon: TrendingUp },
        { label: "Payments Till Date", value: "₹3,20,56,184.64", icon: BarChart3 }
      ],
      image: "/images/growth/myntra-payments.png",
      description: "From ₹0 to ₹3.2 Crore+ in total payments on Myntra"
    },
    {
      platform: "Blinkit",
      icon: "B",
      color: "from-yellow-400 to-orange-500",
      bgColor: "bg-yellow-50",
      title: "Sales Performance",
      stats: [
        { label: "Total Sales (INR)", value: "₹43,76,752", icon: IndianRupee },
        { label: "Total Units Sold", value: "10,448", icon: ShoppingCart },
        { label: "Growth Period", value: "30 Days", icon: TrendingUp },
        { label: "Platform", value: "Blinkit", icon: Target }
      ],
      image: "/images/growth/blinkit-sales.png",
      description: "₹43.76 Lakhs revenue with 10,448 units sold in just 30 days"
    },
    {
      platform: "Myntra Ads",
      icon: "A",
      color: "from-blue-500 to-indigo-500",
      bgColor: "bg-blue-50",
      title: "Product Listing Ads",
      stats: [
        { label: "Impressions", value: "2,28,346", icon: Eye },
        { label: "Clicks", value: "3,870", icon: MousePointer },
        { label: "Total Revenue", value: "₹2,10,695.28", icon: IndianRupee },
        { label: "Overall ROI", value: "15.74x", icon: TrendingUp }
      ],
      image: "/images/growth/myntra-ads.png",
      description: "15.74x ROI on advertising spend with targeted campaigns"
    },
    {
      platform: "Blinkit Ads",
      icon: "B",
      color: "from-green-500 to-emerald-500",
      bgColor: "bg-green-50",
      title: "Brand Central Campaign",
      stats: [
        { label: "Sales", value: "₹1,06,026", icon: IndianRupee },
        { label: "Qty Sold", value: "174", icon: ShoppingCart },
        { label: "RoAS", value: "20.9x", icon: TrendingUp },
        { label: "Impressions", value: "20,668", icon: Eye }
      ],
      image: "/images/growth/blinkit-ads.png",
      description: "20.9x RoAS with efficient ad spend optimization"
    },
    {
      platform: "Blinkit",
      icon: "B",
      color: "from-yellow-500 to-amber-500",
      bgColor: "bg-amber-50",
      title: "Monthly Sales Performance",
      stats: [
        { label: "Total Sales (INR)", value: "₹6,16,204", icon: IndianRupee },
        { label: "Total Units Sold", value: "2,046", icon: ShoppingCart },
        { label: "Growth Period", value: "30 Days", icon: TrendingUp },
        { label: "Platform", value: "Blinkit", icon: Target }
      ],
      image: "/images/growth/blinkit-monthly.png",
      description: "₹6.16 Lakhs monthly sales with 2,046 units on Blinkit"
    },
    {
      platform: "Blinkit",
      icon: "B",
      color: "from-yellow-400 to-yellow-600",
      bgColor: "bg-yellow-50",
      title: "Sales Performance",
      stats: [
        { label: "Total Sales (INR)", value: "₹6,70,072", icon: IndianRupee },
        { label: "Total Units Sold", value: "1,878", icon: ShoppingCart },
        { label: "Growth Period", value: "30 Days", icon: TrendingUp },
        { label: "Platform", value: "Blinkit", icon: Target }
      ],
      image: "/images/growth/blinkit-sales2.png",
      description: "₹6.70 Lakhs revenue with 1,878 units sold in 30 days on Blinkit"
    }
  ];

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % growthMetrics.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + growthMetrics.length) % growthMetrics.length);
  };

  return (
    <section id="growth-story" className="py-16 md:py-24 px-4 bg-gradient-to-br from-gray-50 via-white to-gray-50" data-testid="growth-story-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full text-sm font-bold mb-4">
            <TrendingUp size={16} />
            <span>REAL RESULTS</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Growth <span className="bg-gradient-to-r from-green-500 to-emerald-500 bg-clip-text text-transparent">Story</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            From ₹0 to Lakhs in just 6 months - See real dashboard screenshots from our clients
          </p>
        </div>

        {/* Highlight Banner */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl p-6 md:p-8 text-white text-center mb-12 shadow-xl">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            <div>
              <div className="text-4xl md:text-5xl font-black">₹0</div>
              <div className="text-green-100 text-sm">Starting Point</div>
            </div>
            <div className="flex items-center">
              <TrendingUp className="w-8 h-8 md:w-12 md:h-12 animate-bounce" />
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-black">₹3.2Cr+</div>
              <div className="text-green-100 text-sm">In 6 Months</div>
            </div>
          </div>
        </div>

        {/* Growth Cards Grid */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {growthMetrics.map((metric, idx) => (
            <div 
              key={idx}
              className={`${metric.bgColor} rounded-2xl p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 cursor-pointer group`}
              onClick={() => openLightbox(idx)}
              data-testid={`growth-card-${idx}`}
            >
              {/* Platform Header */}
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${metric.color} flex items-center justify-center text-white font-bold text-xl shadow-lg`}>
                  {metric.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{metric.platform}</h3>
                  <p className="text-sm text-gray-500">{metric.title}</p>
                </div>
                <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center">
                    <Eye className="w-5 h-5 text-gray-600" />
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {metric.stats.map((stat, statIdx) => (
                  <div key={statIdx} className="bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-gray-100">
                    <div className="flex items-center gap-2 mb-1">
                      <stat.icon className="w-4 h-4 text-gray-400" />
                      <span className="text-xs text-gray-500">{stat.label}</span>
                    </div>
                    <div className={`text-lg font-bold bg-gradient-to-r ${metric.color} bg-clip-text text-transparent`}>
                      {stat.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Screenshot Preview */}
              <div className="relative rounded-xl overflow-hidden border border-gray-200 shadow-md group-hover:shadow-lg transition-shadow">
                <img 
                  src={metric.image} 
                  alt={`${metric.platform} Dashboard - ${metric.title}`}
                  className="w-full h-48 object-cover object-top"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-3 shadow-xl">
                    <Eye className="w-6 h-6 text-gray-700" />
                  </div>
                </div>
              </div>

              {/* Description */}
              <p className="mt-4 text-sm text-gray-600 font-medium">{metric.description}</p>
            </div>
          ))}
        </div>

        {/* Customer Video Reviews */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Watch What Our Customers Say
            </h3>
            <p className="text-gray-600">Real testimonials from brands who achieved success with BrandBorn</p>
            <p className="text-sm text-gray-500 mt-2 font-medium">Trusted by brands across India — from <strong className="text-red-600">Patna</strong> to <strong className="text-red-600">Ludhiana</strong>, Mumbai to Bangalore</p>
          </div>
          
          {/* Video Grid - 5 per row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 max-w-7xl mx-auto">
            {videos.map((video) => (
              <div key={video.id} className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-gray-100 hover:border-red-500 hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`video-testimonial-${video.id}`}>
                <div className="relative pb-[177.78%] bg-black">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${video.embed_id || video.embedId}?rel=0&modestbranding=1`}
                    title={`${video.brand} Success Story - ${video.platforms}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
                <div className="p-4 text-center">
                  <div className="flex items-center justify-center gap-1 mb-2">
                    {[1,2,3,4,5].map(i => (
                      <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-black text-sm font-black">{video.brand}</p>
                  <p className="text-gray-500 text-xs mt-1 font-medium">{video.platforms}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">Want similar results for your brand?</p>
          <a 
            href="#contact"
            className="inline-flex items-center gap-2 px-10 py-5 bg-red-600 text-white rounded-2xl font-black text-base tracking-wider uppercase shadow-[0_4px_20px_rgba(220,38,38,0.3)] hover:bg-red-700 hover:shadow-[0_12px_40px_rgba(220,38,38,0.4)] transition-all duration-500 hover:-translate-y-1"
            data-testid="growth-story-cta"
          >
            Launch My Brand Now
            <TrendingUp size={20} />
          </a>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={closeLightbox}>
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={closeLightbox}
          >
            <X size={32} />
          </button>
          
          <button 
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors p-2"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
          >
            <ChevronLeft size={40} />
          </button>
          
          <button 
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors p-2"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
          >
            <ChevronRight size={40} />
          </button>

          <div className="max-w-5xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <img 
              src={growthMetrics[currentImageIndex].image}
              alt={`${growthMetrics[currentImageIndex].platform} Dashboard`}
              className="w-full h-auto rounded-lg"
            />
            <div className="text-center mt-4 text-white">
              <h4 className="text-xl font-bold">{growthMetrics[currentImageIndex].platform} - {growthMetrics[currentImageIndex].title}</h4>
              <p className="text-gray-300 mt-2">{growthMetrics[currentImageIndex].description}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default GrowthStory;
;
